import { Compass, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { Logo } from '../../components/Logo';
import { Menu, anchorFrom, type Anchor } from '../../components/Popover';
import { Tooltip, type TooltipTarget } from '../../components/Tooltip';
import { usersApi } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { isUnread, mentionCount, serverUnread } from '../../store/selectors';
import { markServerRead } from './markRead';
import { ServerIcon } from './ServerIcon';
import { extraMenuItems } from '../extras';

/** Serverleiste: Startseite (DMs), Server mit Ungelesen-/Erwähnungsanzeige, Server hinzufügen. */
export function ServerRail() {
  const { serverId } = useParams();
  const navigate = useNavigate();
  const order = useAppStore((s) => s.serverOrder);
  const servers = useAppStore((s) => s.servers);
  const dmMentions = useAppStore((s) => s.dmIds.reduce((n, id) => n + mentionCount(s, id), 0));
  const dmUnreadIds = useAppStore((s) => s.dmIds.filter((id) => isUnread(s, id) && mentionCount(s, id) > 0).slice(0, 5).join(','));
  const dms = useAppStore((s) => s.channels);
  const readStates = useAppStore((s) => s.readStates);
  const mentionCounts = useMemo(() => Object.fromEntries(Object.values(readStates).map((r) => [r.channelId, r.mentionCount])), [readStates]);
  const [addMenu, setAddMenu] = useState<Anchor | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const userMenuItems = extraMenuItems.filter((i) => i.slot === 'userMenu' && i.to);

  const onDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const next = order.filter((id) => id !== dragId);
    next.splice(next.indexOf(targetId), 0, dragId);
    useAppStore.getState().update((d) => {
      d.serverOrder = next;
    });
    setDragId(null);
    void usersApi.updateSettings({ serverFolders: next.map((id) => ({ id: `f-${id}`, name: null, color: null, serverIds: [id] })) }).catch(() => undefined);
  };

  const unreadDmList = useMemo(() => (dmUnreadIds ? dmUnreadIds.split(',') : []), [dmUnreadIds]);

  return (
    <nav className="server-rail scroll" aria-label="Server" data-testid="server-rail">
      <RailItem active={!serverId} label="Direktnachrichten" badge={dmMentions} testId="rail-home">
        <NavLink to={paths.home()} className="rail-button rail-home" aria-label="Direktnachrichten" data-testid="rail-home-link">
          <Logo size={30} title="Direktnachrichten" />
        </NavLink>
      </RailItem>
      {unreadDmList.map((id) => {
        const ch = dms[id];
        const other = ch?.recipients?.[0];
        if (!ch) return null;
        return (
          <RailItem key={id} active={false} label={ch.name ?? other?.displayName ?? other?.username ?? 'DM'} badge={mentionCounts[id] ?? 0} testId={`rail-dm-${id}`}>
            <NavLink to={paths.dm(id)} className="rail-button">
              <ServerIcon server={{ name: ch.name ?? other?.username ?? 'DM', iconUrl: ch.iconUrl ?? other?.avatarUrl ?? null }} />
            </NavLink>
          </RailItem>
        );
      })}
      <div className="rail-sep" role="separator" />
      <ul className="rail-list" role="list">
        {order.map((id) => {
          const s = servers[id];
          if (!s) return null;
          return (
            <li
              key={id}
              draggable
              onDragStart={() => setDragId(id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(id)}
              data-testid="rail-server"
              data-server-id={id}
            >
              <ServerRailEntry serverId={id} active={serverId === id} />
            </li>
          );
        })}
      </ul>
      <RailItem active={false} label="Server hinzufügen" testId="rail-add">
        <button className="rail-button rail-action" onClick={(e) => setAddMenu(anchorFrom(e.currentTarget))} aria-label="Server hinzufügen" data-testid="rail-add-server">
          <Plus size={22} />
        </button>
      </RailItem>
      {userMenuItems.map((item) => (
        <RailItem key={item.id} active={false} label={item.label} testId={`rail-extra-${item.id}`}>
          <button className="rail-button rail-action" onClick={() => navigate(item.to!)} aria-label={item.label}>
            <Compass size={22} />
          </button>
        </RailItem>
      ))}
      {addMenu && (
        <Menu
          anchor={addMenu}
          placement="right-start"
          onClose={() => setAddMenu(null)}
          testId="add-server-menu"
          items={[
            { id: 'create', label: 'Server erstellen', onSelect: () => overlays.open({ type: 'createServer' }) },
            { id: 'join', label: 'Server beitreten', onSelect: () => overlays.open({ type: 'joinServer' }) },
          ]}
        />
      )}
    </nav>
  );
}

function ServerRailEntry({ serverId, active }: { serverId: string; active: boolean }) {
  const server = useAppStore((s) => s.servers[serverId]);
  const unread = useAppStore((s) => serverUnread(s, serverId).unread);
  const mentions = useAppStore((s) => serverUnread(s, serverId).mentions);
  const navigate = useNavigate();
  const [menu, setMenu] = useState<Anchor | null>(null);
  if (!server) return null;
  const extra = extraMenuItems.filter((i) => i.slot === 'serverMenu');
  return (
    <>
      <RailItem active={active} unread={unread} label={server.name} badge={mentions} testId={`rail-server-${serverId}`}>
        <NavLink
          to={paths.server(serverId)}
          className="rail-button"
          aria-label={server.name}
          aria-current={active ? 'page' : undefined}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenu({ x: e.clientX, y: e.clientY });
          }}
          data-testid="rail-server-link"
        >
          <ServerIcon server={server} />
        </NavLink>
      </RailItem>
      {menu && (
        <Menu
          anchor={menu}
          onClose={() => setMenu(null)}
          testId="rail-server-menu"
          items={[
            { id: 'mark-read', label: 'Als gelesen markieren', disabled: !unread && !mentions, onSelect: () => markServerRead(serverId) },
            { id: 'notifications', label: 'Benachrichtigungen', onSelect: () => overlays.open({ type: 'notificationSettings', serverId }) },
            ...extra.map((it) => ({
              id: `extra-${it.id}`,
              label: it.label,
              onSelect: () => (it.to ? void navigate(it.to.replace(':serverId', serverId)) : overlays.open({ type: 'extra', item: it, ctx: { serverId } })),
            })),
          ]}
        />
      )}
    </>
  );
}

function RailItem({ active, unread, label, badge = 0, children, testId }: { active: boolean; unread?: boolean; label: string; badge?: number; children: TooltipTarget; testId: string }) {
  return (
    <div className={`rail-item ${active ? 'active' : ''} ${unread ? 'unread' : ''}`} data-testid={testId} data-unread={unread ? 'true' : undefined}>
      <span className="rail-pill" aria-hidden="true" />
      <Tooltip label={label} side="right">
        {children}
      </Tooltip>
      {badge > 0 && (
        <span className="badge rail-badge" data-testid="rail-badge" aria-label={`${badge} Erwähnungen`}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </div>
  );
}
